import { getKeyByItemIncludes } from '../helpers/object';
import { DjangoEmployee, Employee, parseEmployee } from './../employee';
import { applyTimezone, parseDate } from './../helpers/date';
import { AbstractLoan, DjangoAbstractLoan } from './../loan/abstract-loan.model';
import { DjangoProposal, parseProposal, Proposal } from './../proposal';
import {
  isServiceGiveupable,
  SERVICE_CCB_DOWNLOADABLE_STATUSES,
  SERVICE_LIQUIDATEABLE_STATUSES,
  SERVICE_STATUS,
} from './../service';
import {
  EMPLOYEE_SERVICE_STATUS_BY_STEP,
  EMPLOYEE_SERVICE_STEP,
  HR_SERVICE_STATUS_BY_STEP,
  HR_SERVICE_STEP,
} from './../service/service-steps.model';

export class PaycheckAdvance implements AbstractLoan {
  processingStatus: SERVICE_STATUS;
  paymentStatus: string;
  employee: Employee;
  proposal: Proposal;
  registration: string;
  invoice: string;
  credited: Date;
  dueDate: Date;
  taxAmount: number;
  totalDebtAmount: number;
  // ABSTRACT LOAN
  created: Date;
  modified: Date;
  amount: number;
  period: number;
  interestRate: number;
  effectiveInterestRate: number;
  feeAmount: number;
  currency: string;
  score: number;
  scoreVersion: number;
  agioAmount: number;

  constructor(data: string | PaycheckAdvance | DjangoPaycheckAdvance = {}) {
    if (data instanceof PaycheckAdvance) {
      bindPaycheckAdvanceData(this, data);
    } else if (typeof data === 'string') {
      bindDjangoPaycheckAdvanceData(this, { proposal: data });
    } else {
      bindDjangoPaycheckAdvanceData(this, data);
    }
  }

  get giveupable(): boolean {
    return giveupable(this);
  }

  get liquidatable(): boolean {
    return liquidatable(this);
  }

  get ccbDownloadable(): boolean {
    return ccbDownloadable(this);
  }

  get employeeStep(): EMPLOYEE_SERVICE_STEP {
    return getKeyByItemIncludes<EMPLOYEE_SERVICE_STEP>(
      EMPLOYEE_SERVICE_STATUS_BY_STEP,
      this.processingStatus,
    );
  }

  get hrStep(): HR_SERVICE_STEP {
    return getKeyByItemIncludes<HR_SERVICE_STEP>(HR_SERVICE_STATUS_BY_STEP, this.processingStatus);
  }
}

export function parsePaycheckAdvance(data: DjangoPaycheckAdvance | PaycheckAdvance) {
  if (data) {
    return new PaycheckAdvance(data);
  } else {
    return undefined;
  }
}

function bindPaycheckAdvanceData(paycheckAdvance: PaycheckAdvance, data: PaycheckAdvance) {
  paycheckAdvance.processingStatus = data.processingStatus;
  paycheckAdvance.paymentStatus = data.paymentStatus;
  paycheckAdvance.employee = data.employee;
  paycheckAdvance.proposal = data.proposal;
  paycheckAdvance.registration = data.registration;
  paycheckAdvance.invoice = data.invoice;
  paycheckAdvance.dueDate = data.dueDate;
  paycheckAdvance.credited = data.credited;
  paycheckAdvance.taxAmount = data.taxAmount;
  paycheckAdvance.totalDebtAmount = data.totalDebtAmount;
  // ABSTRACT LOAN
  paycheckAdvance.created = data.created;
  paycheckAdvance.modified = data.modified;
  paycheckAdvance.amount = data.amount;
  paycheckAdvance.period = data.period;
  paycheckAdvance.interestRate = data.interestRate;
  paycheckAdvance.effectiveInterestRate = data.effectiveInterestRate;
  paycheckAdvance.feeAmount = data.feeAmount;
  paycheckAdvance.currency = data.currency;
  paycheckAdvance.score = data.score;
  paycheckAdvance.scoreVersion = data.scoreVersion;
  paycheckAdvance.agioAmount = data.agioAmount;
}

//////////////////////
/// DJANGO SUPPORT ///
//////////////////////
export interface DjangoPaycheckAdvance extends DjangoAbstractLoan {
  processing_status?: SERVICE_STATUS;
  payment_status?: string;
  employee?: string | DjangoEmployee;
  proposal?: string | DjangoProposal;
  registration?: string;
  invoice?: string;
  due_date?: string;
  created?: string;
  tax_amount?: number;
  total_debt_amount?: number;
  // ABSTRACT LOAN
  modified?: string;
  credited?: string;
  amount?: number;
  period?: number;
  interest_rate?: number;
  effective_interest_rate?: number;
  fee_amount?: number;
  currency?: string;
  score?: number;
  score_version?: number;
  agio_amount?: number;
}

function bindDjangoPaycheckAdvanceData(
  paycheckAdvance: PaycheckAdvance,
  data: DjangoPaycheckAdvance,
) {
  paycheckAdvance.processingStatus = data.processing_status;
  paycheckAdvance.paymentStatus = data.payment_status;
  paycheckAdvance.employee = parseEmployee(data.employee);
  paycheckAdvance.proposal = parseProposal(data.proposal);
  paycheckAdvance.registration = data.registration;
  paycheckAdvance.invoice = data.invoice;
  paycheckAdvance.dueDate = applyTimezone(parseDate(data.due_date), 0);
  paycheckAdvance.created = parseDate(data.created);
  paycheckAdvance.taxAmount = data.tax_amount;
  paycheckAdvance.totalDebtAmount = data.total_debt_amount;
  // ABSTRACT LOAN
  paycheckAdvance.modified = parseDate(data.modified);
  paycheckAdvance.credited = parseDate(data.credited);
  paycheckAdvance.amount = data.amount;
  paycheckAdvance.period = data.period;
  paycheckAdvance.interestRate = data.interest_rate;
  paycheckAdvance.effectiveInterestRate = data.effective_interest_rate;
  paycheckAdvance.feeAmount = data.fee_amount;
  paycheckAdvance.currency = data.currency;
  paycheckAdvance.score = data.score;
  paycheckAdvance.scoreVersion = data.score_version;
  paycheckAdvance.agioAmount = data.agio_amount;
}

///////////////
/// HELPERS ///
///////////////
function giveupable(paycheckAdvance: PaycheckAdvance): boolean {
  return isServiceGiveupable(paycheckAdvance);
}

function liquidatable(paycheckAdvance: PaycheckAdvance): boolean {
  return SERVICE_LIQUIDATEABLE_STATUSES.includes(paycheckAdvance.processingStatus);
}

function ccbDownloadable(paycheckAdvance: PaycheckAdvance): boolean {
  return SERVICE_CCB_DOWNLOADABLE_STATUSES.includes(paycheckAdvance.processingStatus);
}
